import React, { createContext, useContext, useState, useEffect } from 'react';
import client from '../services/api';
import { useLazyQuery, useMutation } from '@apollo/client';
import { GET_PORTFOLIOS } from '../graphql/queries/portfolioSummary';
import { CREATE_PORTFOLIO } from '../graphql/mutations/portfolios';
import { useAuthContext } from './AuthContext';
import toast from 'react-hot-toast';
import CreatePortfolioModal from '../components/CreatePortfolioModal';

const PortfolioContext = createContext({});

export const usePortfolioContext = () => {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolioContext must be used within a PortfolioProvider');
  }
  return context;
};

export const PortfolioProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuthContext();
  const [currentPortfolio, setCurrentPortfolio] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [portfoliosLoaded, setPortfoliosLoaded] = useState(false);
  
  // Create portfolio modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState('');
  
  // Create portfolio mutation
  const [createPortfolio, { loading: creating }] = useMutation(CREATE_PORTFOLIO);

  // Load saved portfolio from localStorage
  const loadSavedPortfolio = () => {
    try {
      const savedPortfolioId = localStorage.getItem('selectedPortfolioId');
      const savedPortfolioCurrency = localStorage.getItem('selectedPortfolioCurrency');
      return { id: savedPortfolioId, currency: savedPortfolioCurrency };
    } catch (error) {
      console.error('Error loading saved portfolio:', error);
      return { id: null, currency: null };
    }
  };

  // Save portfolio to localStorage
  const savePortfolioToStorage = (portfolioId, currency) => {
    try {
      console.log('📝 Saving portfolio ID to localStorage:', portfolioId);
      if (portfolioId) {
        localStorage.setItem('selectedPortfolioId', portfolioId);
        console.log('✅ Saved successfully');
        if (currency) {
          localStorage.setItem('selectedPortfolioCurrency', currency);
        }
      } else {
        localStorage.removeItem('selectedPortfolioId');
        localStorage.removeItem('selectedPortfolioCurrency');
        console.log('🗑️ Removed from localStorage');
      }
    } catch (error) {
      console.error('❌ Error saving portfolio:', error);
    }
  };



  // Lazy query to get all portfolios when needed
  const [getPortfolios, {
    loading: portfoliosLoading,
    error: portfoliosError,
  }] = useLazyQuery(GET_PORTFOLIOS, {
    onCompleted: (data) => {
      console.log('🏦 Portfolios loaded:', data?.portfolios?.length || 0);
      setPortfoliosLoaded(true);
      
      if (data?.portfolios) {
        setPortfolios(data.portfolios);
        
        // Always try to restore from localStorage first on portfolio load
        const { id: savedPortfolioId, currency: savedCurrency } = loadSavedPortfolio();
        console.log('💾 Saved portfolio ID from localStorage:', savedPortfolioId);
        
        if (savedPortfolioId) {
          const savedPortfolio = data.portfolios.find(p => p.id === savedPortfolioId);
          if (savedPortfolio) {
            console.log('✅ Restored portfolio from localStorage:', savedPortfolio.name);
            setCurrentPortfolio(savedCurrency ? { ...savedPortfolio, currency: savedCurrency } : savedPortfolio);
            return; // Early return - we successfully restored
          } else {
            console.log('❌ Saved portfolio not found in list, falling back to first');
          }
        } else {
          console.log('📭 No saved portfolio ID found');
        }
        
        // Fallback: select first portfolio if we couldn't restore or no saved portfolio
        if (data.portfolios.length > 0) {
          console.log('🎯 Selecting first portfolio as fallback:', data.portfolios[0].name);
          setCurrentPortfolio(data.portfolios[0]);
          savePortfolioToStorage(data.portfolios[0].id, data.portfolios[0].currency);
        }
      }
    },
    onError: (error) => {
      console.error('Error fetching portfolios:', error);
      setPortfoliosLoaded(true);
    },
  });

  // Load portfolios when authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      getPortfolios();
    }
  }, [isAuthenticated, authLoading, getPortfolios]);

  // Reset state when user logs out (but not during initial loading)
  useEffect(() => {
    if (!isAuthenticated && !authLoading) {
      console.log('🚪 User logged out, clearing portfolio state');
      setCurrentPortfolio(null);
      setPortfolios([]);
      savePortfolioToStorage(null); // Clear saved portfolio
      setPortfoliosLoaded(false);
      try { client.clearStore(); } catch (_) {}
    }
  }, [isAuthenticated, authLoading]);

  const selectPortfolio = (portfolio) => {
    if (!portfolio) return;
    // Prefer currency from Apollo cache (e.g. updated by Header currency dropdown) so switching back keeps selected currency
    try {
      const { data } = client.readQuery({ query: GET_PORTFOLIOS });
      const fromCache = data?.portfolios?.find((p) => p.id === portfolio.id);
      if (fromCache?.currency) {
        portfolio = { ...portfolio, currency: fromCache.currency };
      }
    } catch (_) {
      // Cache may not be populated
    }
    console.log('🔄 Selecting portfolio:', portfolio?.name, 'ID:', portfolio?.id);
    setCurrentPortfolio(portfolio);
    // Save to localStorage for persistence
    savePortfolioToStorage(portfolio?.id, portfolio?.currency);
    console.log('💾 Saved to localStorage, verifying:', localStorage.getItem('selectedPortfolioId'));
    // Optionally, reset store to drop cross-portfolio artifacts
    try { client.cache.gc(); } catch (_) {}
  };



  const refreshPortfolios = async () => {
    try {
      setPortfolioLoading(true);
      await getPortfolios();
    } catch (error) {
      console.error('Error refreshing portfolios:', error);
    } finally {
      setPortfolioLoading(false);
    }
  };

  // Create portfolio functions
  const openCreateModal = () => {
    setNewPortfolioName('');
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewPortfolioName('');
  };

  const handleCreatePortfolio = async () => {
    if (!newPortfolioName.trim()) {
      toast.error('Please enter a portfolio name');
      return;
    }

    try {
      const { data } = await createPortfolio({
        variables: { name: newPortfolioName.trim() }
      });

      if (data?.createPortfolio?.success) {
        toast.success('Portfolio created successfully!');
        try {
          const evt = new CustomEvent('portfolio:created', { detail: { portfolio: data.createPortfolio.portfolio } });
          window.dispatchEvent(evt);
        } catch (_) {}
        closeCreateModal();
        // Refresh portfolios list
        await refreshPortfolios();
        if (data.createPortfolio.portfolio) {
          selectPortfolio(data.createPortfolio.portfolio);
        }
      } else {
        toast.error(data?.createPortfolio?.error || 'Failed to create portfolio');
      }
    } catch (error) {
      toast.error('Error creating portfolio');
      console.error('Create portfolio error:', error);
    }
  };

  const value = {
    currentPortfolio,
    currentPortfolioId: currentPortfolio?.id,
    portfolios,
    loading: portfoliosLoading || portfolioLoading,
    error: portfoliosError,
    selectPortfolio,
    refreshPortfolios,
    portfoliosLoaded,
    isAuthenticated,
    // Create portfolio modal
    showCreateModal,
    newPortfolioName,
    creating,
    openCreateModal,
    closeCreateModal,
    handleCreatePortfolio,
    setNewPortfolioName,
  };

  return (
    <PortfolioContext.Provider value={value}>
      {children}
      
      <CreatePortfolioModal
        isOpen={showCreateModal}
        name={newPortfolioName}
        onChangeName={setNewPortfolioName}
        onCancel={closeCreateModal}
        onCreate={handleCreatePortfolio}
        creating={creating}
      />
    </PortfolioContext.Provider>
  );
}; 